import React, { useState } from 'react';
import {
  Box, Button, TextField, Typography,
  Container, Alert, CircularProgress
} from '@mui/material';
import { ArrowBack, Email } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'https://imara-production-91ab.up.railway.app';

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: '#4ade80' },
    '&.Mui-focused fieldset': { borderColor: '#4ade80' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#4ade80' },
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSubmit() {
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: email.trim() });
      setSuccess(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Container maxWidth="xs">
        <Box sx={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: 4,
          p: 4,
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          {/* Logo */}
          <Typography variant="h3" sx={{
            textAlign: 'center', color: '#4ade80',
            fontWeight: 900, mb: 0.5,
          }}>
            🌿 Imara
          </Typography>

          {!success ? (
            <>
              <Typography sx={{
                textAlign: 'center', color: 'rgba(255,255,255,0.5)',
                mb: 1, fontSize: '0.85rem',
              }}>
                Forgot your password?
              </Typography>
              <Typography sx={{
                textAlign: 'center', color: 'rgba(255,255,255,0.35)',
                mb: 4, fontSize: '0.78rem',
              }}>
                Enter your email and we'll send you a reset link.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              )}

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                sx={inputStyle}
                margin="normal"
              />

              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? null : <Email />}
                sx={{
                  mt: 3, py: 1.5,
                  background: 'linear-gradient(90deg, #4ade80, #22d3ee)',
                  color: '#000', fontWeight: 700, borderRadius: 2,
                  '&:hover': { background: 'linear-gradient(90deg, #22d3ee, #4ade80)' },
                  '&.Mui-disabled': { background: 'rgba(255,255,255,0.1)', color: '#555' },
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
              </Button>
            </>
          ) : (
            /* Success state */
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography sx={{ fontSize: '3rem', mb: 2 }}>📧</Typography>
              <Typography sx={{
                color: '#4ade80', fontWeight: 700,
                fontSize: '1.1rem', mb: 1,
              }}>
                Reset link sent!
              </Typography>
              <Typography sx={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.85rem', mb: 3, lineHeight: 1.6,
              }}>
                Check your email at <strong style={{ color: '#e8f4f8' }}>{email}</strong> and click the reset link. It expires in 30 minutes.
              </Typography>
              <Typography sx={{
                color: 'rgba(255,255,255,0.3)',
                fontSize: '0.75rem',
              }}>
                Didn't receive it? Check your spam folder.
              </Typography>
            </Box>
          )}

          {/* Back to login */}
          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 0.5, mt: 3, cursor: 'pointer',
              color: 'rgba(255,255,255,0.4)',
              '&:hover': { color: '#4ade80' },
              transition: 'color 0.2s',
            }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '0.85rem' }}>Back to Login</Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}